import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import Footer from "components/Footer";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import MDBox from "mdComponents/MDBox";
import MDTypography from "mdComponents/MDTypography";
import MDButton from "mdComponents/MDButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { formatDate } from "../../Utils";
import locationSuspendService from "../../services/locationSuspendService";
import NotificationAlert from "controls/NotificationAlert";
import { useHostPortalContext } from "context/HostPortalContext";
import { useNavigationMessageContext } from "context/NavigationMessageContext";
import LoadingSpinner from "controls/LoadingSpinner";

const LocationSuspendedUsers: React.FC = () => {
  const { id } = useParams<{ id: any }>();
  const { host } = useHostPortalContext();
  const { setMessage } = useNavigationMessageContext();
  const [loading, setLoading] = useState(true);
  const [locationSuspends, setLocationSuspends] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);

  const [showSnackBar, setShowSnackBar] = useState(false);
  const [snackbar, setSnackbar] = useState({
    color: "success",
    content: "Saved Successfully!",
    title: "Save Successful",
    icon: "check",
    dateTime: "just now",
  });

  const openSnackBar = () => setShowSnackBar(true);
  const closeSnackBar = () => setShowSnackBar(false);

  useEffect(() => {
    setMessage(`${host?.name} Suspended / Banned Users`);
  }, []);

  useEffect(() => {
    const loadLocationSuspends = async () => {
      setLoading(true);
      try {
        const { data } = await locationSuspendService.fetchUserLocationSuspends(host ? host.id : id);
        setLocationSuspends(data ?? []);
      } finally {
        setLoading(false);
      }
    };
    loadLocationSuspends();
  }, [host, id]);

  const onViewDetails = (item: any) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const onModalClose = () => {
    setShowModal(false);
    setSelectedItem(null);
  };

  const liftSuspension = async () => {
    if (!selectedItem) return;
    const result = await locationSuspendService.liftUserLocationSuspend(selectedItem.id);
    if (result === true) {
      setSnackbar({
        color: "success",
        content: `${selectedItem.isBan ? "Ban" : "Suspension"} lifted. User may now use this location.`,
        title: "Action Successful",
        icon: "check",
        dateTime: "just now",
      });
      openSnackBar();
      onModalClose();
      // reload list
      const { data } = await locationSuspendService.fetchUserLocationSuspends(host.id);
      setLocationSuspends(data ?? []);
    } else {
      setSnackbar({
        color: "error",
        content: "There was a problem lifting the suspension/ban. Please try again.",
        title: "Action Failed",
        icon: "error",
        dateTime: "just now",
      });
      openSnackBar();
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pb={3}>
        {loading ? (
        <Grid container spacing={6} justifyContent="center">
            <Grid item xs={12} display="flex" justifyContent="center">
            <Card sx={{ p: 3, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <LoadingSpinner title="Loading Suspended Users..." size={30} />
            </Card>
            </Grid>
        </Grid>
        ) : locationSuspends.length > 0 ? (
          <Grid container justifyContent="center">
            <Grid item xs={12} xl={12}>
                <MDTypography variant="h6" mb={1}>
                 These users have been suspended or banned from using {host?.name ?? "this location"}. This location will not show for them in the app until their suspension/ban is lifted.
                </MDTypography>

                {locationSuspends.map((item) => (
                  <MDBox
                    key={item.id}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    p={2}
                    mb={1}
                    sx={{
                      backgroundColor: "#f9f9f9",
                      borderRadius: "8px",
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "#efefef" },
                    }}
                    onClick={() => onViewDetails(item)}
                  >
                    <MDBox display="flex" alignItems="center" gap={2}>
                      <img
                        src={item.profiles.profilePic}
                        alt={`${item.profiles.firstName} ${item.profiles.lastName}`}
                        width={50}
                        height={50}
                        style={{ borderRadius: "50%", objectFit: "cover" }}
                      />
                      <MDBox>
                        <MDTypography variant="h6">
                          {item.profiles.firstName} {item.profiles.lastName}
                        </MDTypography>
                        <MDTypography variant="body2">
                          {item.isBan ? "Banned" : "Suspended"} on {formatDate(item.created_at, true)}
                        </MDTypography>
                        {!item.isBan && (
                          <MDTypography variant="body2">
                            Suspended for {item.lengthInDays === 360 ? "1 year" : `${item.lengthInDays} days`}
                          </MDTypography>
                        )}
                        <MDTypography variant="body2">Reason: {item.reason}</MDTypography>
                      </MDBox>
                    </MDBox>

                    {/* <MDButton size="small" color="info" onClick={() => onViewDetails(item)}>
                      View
                    </MDButton> */}
                  </MDBox>
                ))}
            </Grid>
          </Grid>
        ) : (
        <Grid container spacing={6} justifyContent="center">
            <Grid item xs={12} display="flex" justifyContent="center">
                <Card sx={{ p: 3, display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <MDBox display="flex" justifyContent="center" alignItems="center">
                        <MDTypography variant="h6">No suspended users for this location.</MDTypography>
                    </MDBox>
                </Card>
            </Grid>
        </Grid>
        )}

        {/* DETAILS MODAL */}
        <Dialog open={showModal} onClose={onModalClose} fullWidth maxWidth="sm">
          <DialogTitle>
            {selectedItem?.isBan ? "Banned" : "Suspended"}: {selectedItem?.profiles.firstName}
          </DialogTitle>
          <DialogContent>
            {selectedItem && (
              <MDBox>
                <MDBox display="flex" alignItems="center" gap={2} mb={2}>
                  <img
                    src={selectedItem.profiles.profilePic}
                    alt={`${selectedItem.profiles.firstName} ${selectedItem.profiles.lastName}`}
                    width={50}
                    height={50}
                    style={{ borderRadius: "50%", objectFit: "cover" }}
                  />
                  <MDBox>
                    <MDTypography variant="h6">
                      {selectedItem.profiles.firstName} {selectedItem.profiles.lastName}
                    </MDTypography>
                    <MDTypography variant="body2">
                      {selectedItem.isBan ? "Banned" : "Suspended"} on {formatDate(selectedItem.created_at, true)}
                    </MDTypography>
                    {!selectedItem.isBan && (
                      <MDTypography variant="body2">
                        Suspended for {selectedItem.lengthInDays === 360 ? "1 year" : `${selectedItem.lengthInDays} days`}
                      </MDTypography>
                    )}
                    <MDTypography variant="body2">Reason: {selectedItem.reason}</MDTypography>
                    <MDTypography variant="body2">Notes: {selectedItem.notes}</MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" variant="outlined" onClick={onModalClose}>
              Done
            </MDButton>
            <MDButton color="dark" onClick={liftSuspension}>
              Lift {selectedItem?.isBan ? "Ban" : "Suspension"}
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Notification */}
        <NotificationAlert
          open={showSnackBar}
          onClose={closeSnackBar}
          close={closeSnackBar}
          color={snackbar.color}
          icon={snackbar.icon}
          title={snackbar.title}
          content={snackbar.content}
          dateTime={snackbar.dateTime}
        />
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default LocationSuspendedUsers;
